import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Users } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { Organization } from '@/lib/types';
import { ORG_VS_PROGRAM_INFO } from '@/lib/constants';
import { useAuth } from '@/lib/auth';
import { InviteLinkBox } from '@/components/ui/InviteLinkBox';
import { InfoBox, PageHeader } from '@/components/layout/PageHeader';
import { Badge, Button, Card, Input, Spinner, Textarea } from '@/components/ui';

export function DashboardPage() {
  const { user, effectiveIsSuperAdmin } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    donationLink: '',
    youtubeLink: '',
    readingSourceLink: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: Organization[] }>('/organizations'),
  });

  async function createOrganization(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api('/organizations', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setShowCreate(false);
      setForm({ name: '', description: '', donationLink: '', youtubeLink: '', readingSourceLink: '' });
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Organizasyon oluşturulamadı');
    } finally {
      setLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const organizations = data?.organizations ?? [];

  return (
    <div className="space-y-4">
      <PageHeader title="Organizasyonlar" subtitle="Grubunuz ve programlarınız" />

      <div className="grid gap-3">
        <InfoBox title={ORG_VS_PROGRAM_INFO.organization.title}>
          {ORG_VS_PROGRAM_INFO.organization.description}
        </InfoBox>
        <InfoBox title={ORG_VS_PROGRAM_INFO.program.title}>
          {ORG_VS_PROGRAM_INFO.program.description}
        </InfoBox>
      </div>

      <div className="flex items-center justify-end">
        {effectiveIsSuperAdmin && (
          <Button size="sm" variant="gold" onClick={() => setShowCreate(!showCreate)}>
            <Plus className="h-4 w-4" />
            Yeni Organizasyon Ekle
          </Button>
        )}
      </div>

      {!effectiveIsSuperAdmin && (
        <Card ornate={false} className="border-amber-200 bg-amber-50/80">
          <p className="text-sm text-amber-950">
            Organizasyon oluşturmak için super admin yetkisi gerekir. Size gönderilen davet linki ile katılabilirsiniz.
          </p>
        </Card>
      )}

      {showCreate && user?.isSuperAdmin && (
        <Card>
          <form onSubmit={createOrganization} className="space-y-3">
            <Input
              placeholder="Organizasyon adı (ör. Barla Grubu)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              minLength={2}
            />
            <Textarea
              placeholder="Açıklama"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" variant="gold" disabled={loading}>
              {loading ? <Spinner /> : 'Ekle'}
            </Button>
          </form>
        </Card>
      )}

      {organizations.length === 0 ? (
        <Card className="text-center">
          <Users className="mx-auto mb-2 h-10 w-10 text-accent" />
          <p className="text-muted-foreground">Henüz organizasyon yok</p>
          <p className="mt-1 text-xs text-muted-foreground">Davet linki ile katıldıysanız yönetici onayı bekliyor olabilirsiniz.</p>
        </Card>
      ) : (
        organizations.map((org) => (
          <Card key={org.id}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-serif text-lg font-semibold text-primary">{org.name}</h3>
                {org.description && (
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{org.description}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {org.donationLink && (
                    <a href={org.donationLink} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">
                      Bağış
                    </a>
                  )}
                  {org.youtubeLink && (
                    <a href={org.youtubeLink} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">
                      YouTube
                    </a>
                  )}
                  {org.readingSourceLink && (
                    <a href={org.readingSourceLink} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">
                      Okuma Kaynağı
                    </a>
                  )}
                </div>
              </div>
              <Badge variant="gold">Organizasyon</Badge>
            </div>

            <div className="mt-4">
              <InviteLinkBox
                inviteCode={org.inviteCode}
                label="Organizasyon Davet Linki"
                onRegenerate={
                  effectiveIsSuperAdmin
                    ? async () => {
                        const res = await api<{ inviteCode: string }>(
                          `/organizations/${org.id}/regenerate-invite`,
                          { method: 'POST' },
                        );
                        await refetch();
                        return res.inviteCode;
                      }
                    : undefined
                }
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link to={`/events?org=${org.id}`}>
                <Button variant="outline" className="w-full" size="sm">
                  Programlar
                </Button>
              </Link>
              <Link to={`/org/${org.id}/edit`}>
                <Button variant="outline" className="w-full" size="sm">
                  <Pencil className="h-3 w-3" />
                  Düzenle
                </Button>
              </Link>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
